import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import {
  Plus,
  Search,
  Pencil,
  Trash2,
  Power,
  PowerOff,
  ArrowRightLeft,
  Loader2,
  Save,
  Filter,
} from 'lucide-react';
import { useInstitution } from '../../contexts/InstitutionContext';
import { useAuth } from '../../contexts/AuthContext';
import { usePermissao } from '../../hooks/usePermissao';
import { Card, Input, Select, Button, Modal, Badge } from '../../Components/ui';
import { useConfirm, useToast } from '../../Components/Feedback';
import { API_URL } from '../../api/config';
import { useApi } from '../../api/useApi';

// Filtro por coluna estilo Excel (mesmo espírito do FiltroColuna de
// AjusteGrade.js, simplificado — sem rascunho/Aplicar, já que aqui a lista é
// pequena e refiltrar na hora é barato): mostra só os valores que realmente
// aparecem naquela coluna, marca/desmarca aplica o filtro imediatamente.
//
// `excluidos` guarda o INVERSO do que aparece marcado (quais valores ficam de
// FORA) — não a lista do que é mostrado. Assim "vazio" sempre quer dizer
// "sem filtro, mostra tudo" sem ambiguidade, e dá pra representar "esconder
// tudo" (excluidos = todas as opções) sem confundir com "não filtrado".
function FiltroColunaSimples({ opcoes, excluidos, onChange }) {
  const [aberto, setAberto] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    if (!aberto) return;
    const fecharSeFora = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setAberto(false);
    };
    document.addEventListener('mousedown', fecharSeFora);
    return () => document.removeEventListener('mousedown', fecharSeFora);
  }, [aberto]);

  const alternar = (valor) => {
    onChange(
      excluidos.includes(valor) ? excluidos.filter((v) => v !== valor) : [...excluidos, valor],
    );
  };

  const ativo = excluidos.length > 0;

  return (
    <span className="relative inline-block ml-1" ref={ref}>
      <button
        type="button"
        onClick={() => setAberto((a) => !a)}
        title="Filtrar"
        className={`w-4 h-4 inline-flex items-center justify-center rounded transition-colors ${ativo ? 'text-blue-600 dark:text-blue-400' : 'text-gray-400 dark:text-gray-500 hover:text-gray-600 dark:hover:text-gray-300'}`}
      >
        <Filter className="w-3 h-3" fill={ativo ? 'currentColor' : 'none'} />
      </button>
      {aberto && (
        <div className="absolute z-30 top-full mt-1 left-1/2 -translate-x-1/2 w-36 bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-700 rounded-lg shadow-lg normal-case">
          <div className="flex items-center justify-between px-2 pt-2 pb-1 border-b border-gray-100 dark:border-gray-800">
            <button
              type="button"
              onClick={() => onChange([])}
              className="text-[10px] font-medium text-blue-600 dark:text-blue-400 hover:underline"
            >
              Todas
            </button>
            <button
              type="button"
              onClick={() => onChange(opcoes)}
              className="text-[10px] font-medium text-gray-400 dark:text-gray-500 hover:underline"
            >
              Nenhuma
            </button>
          </div>
          <div className="px-2 py-1.5 space-y-1 max-h-48 overflow-y-auto">
            {opcoes.map((valor) => (
              <label
                key={valor}
                className="flex items-center gap-1.5 text-[11px] font-normal text-gray-700 dark:text-gray-300 cursor-pointer"
              >
                <input
                  type="checkbox"
                  checked={!excluidos.includes(valor)}
                  onChange={() => alternar(valor)}
                  className="rounded border-gray-300 dark:border-gray-600 text-blue-600 focus:ring-blue-500/30"
                />
                <span className="truncate">{valor}</span>
              </label>
            ))}
          </div>
        </div>
      )}
    </span>
  );
}

// CRUD de educador: criar, renomear, ativar/desativar, apagar (só quem nunca
// deu aula) e transferir em massa todas as turmas ativas de um pra outro —
// sem isso, quando um educador sai, reatribuir as turmas dele era 100%
// manual, turma por turma, em Turmas.js.
function Professores() {
  const { institutionId } = useInstitution();
  const { token } = useAuth();
  const { podeCriar, podeEditar, podeExcluir } = usePermissao('/professores');
  const queryClient = useQueryClient();
  const confirm = useConfirm();
  const toast = useToast();

  const authHeaders = useMemo(
    () => ({
      'Content-Type': 'application/json',
      'x-institution-id': institutionId,
      Authorization: `Bearer ${token}`,
    }),
    [institutionId, token],
  );
  const api = useApi();

  const [busca, setBusca] = useState('');
  // Filtro por coluna (estilo Excel, ver FiltroColunaSimples acima) — cada
  // chave guarda os VALORES EXCLUÍDOS daquela coluna, vazio = sem filtro.
  const [filtrosColuna, setFiltrosColuna] = useState({ status: [], turmas: [], inscritos: [] });

  const { data: professores = [], isLoading } = useQuery({
    queryKey: ['professores-admin', institutionId],
    queryFn: async () => {
      const res = await api.get(`/api/professores-admin`);
      return res?.data ?? (Array.isArray(res) ? res : []);
    },
    enabled: !!institutionId && !!token,
    refetchOnMount: 'always',
    refetchOnWindowFocus: 'always',
  });

  const recarregar = () =>
    queryClient.invalidateQueries({ queryKey: ['professores-admin', institutionId] });

  // Valores que realmente aparecem em cada coluna filtrável — só esses viram
  // opção no filtro (igual Excel, nunca oferece um valor que não existe).
  const opcoesStatus = useMemo(() => {
    const presentes = new Set(professores.map((p) => (p.ativo ? 'Ativo' : 'Inativo')));
    return ['Ativo', 'Inativo'].filter((v) => presentes.has(v));
  }, [professores]);
  const opcoesTurmas = useMemo(
    () =>
      [...new Set(professores.map((p) => p.total_turmas_ativas))].sort((a, b) => a - b).map(String),
    [professores],
  );
  const opcoesInscritos = useMemo(
    () => [...new Set(professores.map((p) => p.total_alunos))].sort((a, b) => a - b).map(String),
    [professores],
  );

  const alterarFiltroColuna = (coluna, excluidos) =>
    setFiltrosColuna((prev) => ({ ...prev, [coluna]: excluidos }));
  const filtrosColunaAtivos = Object.values(filtrosColuna).some((v) => v.length > 0);
  const limparFiltrosColuna = () => setFiltrosColuna({ status: [], turmas: [], inscritos: [] });

  const professoresFiltrados = useMemo(() => {
    const termo = busca.toLowerCase();
    return professores.filter((p) => {
      if (termo && !p.nome.toLowerCase().includes(termo)) return false;
      if (filtrosColuna.status.includes(p.ativo ? 'Ativo' : 'Inativo')) return false;
      if (filtrosColuna.turmas.includes(String(p.total_turmas_ativas))) return false;
      if (filtrosColuna.inscritos.includes(String(p.total_alunos))) return false;
      return true;
    });
  }, [professores, busca, filtrosColuna]);

  // --- Criar / Editar ---
  const [modalAberto, setModalAberto] = useState(false);
  const [editando, setEditando] = useState(null);
  const [nomeForm, setNomeForm] = useState('');
  // Nome LEGAL completo, opcional — só usado no Relatório de Ponto em PDF
  // (ver backend/relatorio-ponto-pdf.js); o `nome` continua sendo o curto
  // usado em todo o resto do sistema (dropdown de Pontos, Turmas, Grade...).
  const [nomeCompletoForm, setNomeCompletoForm] = useState('');
  const [salvando, setSalvando] = useState(false);

  const abrirNovo = () => {
    setEditando(null);
    setNomeForm('');
    setNomeCompletoForm('');
    setModalAberto(true);
  };
  const abrirEditar = (p) => {
    setEditando(p);
    setNomeForm(p.nome);
    setNomeCompletoForm(p.nome_completo || '');
    setModalAberto(true);
  };

  const salvar = async (e) => {
    e.preventDefault();
    const nome = nomeForm.trim();
    if (!nome) return;
    setSalvando(true);
    try {
      const url = editando
        ? `${API_URL}/api/professores-admin/${editando.id}`
        : `${API_URL}/api/professores-admin`;
      const method = editando ? 'PATCH' : 'POST';
      const res = await fetch(url, {
        method,
        headers: authHeaders,
        body: JSON.stringify({ nome, nome_completo: nomeCompletoForm.trim() }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Erro ao salvar educador.');
      setModalAberto(false);
      const parecido = data?.data?.parecido_com ?? data?.parecido_com;
      if (parecido) {
        toast.warning(
          `Educador salvo, mas o nome é parecido com "${parecido}" já cadastrado — confira se não é a mesma pessoa.`,
          8000,
        );
      } else {
        toast.success(data?.message || (editando ? 'Educador atualizado.' : 'Educador criado.'));
      }
      recarregar();
    } catch (error) {
      toast.error(error.message);
    } finally {
      setSalvando(false);
    }
  };

  // --- Ativar / Desativar ---
  const alternarAtivo = async (p) => {
    try {
      const res = await api.patch(`/api/professores-admin/${p.id}`, { ativo: !p.ativo });
      toast.success(res?.message || (p.ativo ? 'Educador desativado.' : 'Educador reativado.'));
      recarregar();
    } catch (error) {
      toast.error(error.message);
    }
  };

  // --- Apagar ---
  const apagar = async (p) => {
    if (
      !(await confirm(
        `Apagar o educador "${p.nome}"? Só é possível se ele nunca tiver dado aula em nenhuma turma.`,
        { title: 'Apagar educador', confirmText: 'Apagar' },
      ))
    )
      return;
    try {
      const res = await api.del(`/api/professores-admin/${p.id}`);
      toast.success(res?.message || 'Educador apagado.');
      recarregar();
    } catch (error) {
      toast.error(error.message);
    }
  };

  // --- Transferir turmas ---
  const [modalTransferirAberto, setModalTransferirAberto] = useState(false);
  const [origemTransferencia, setOrigemTransferencia] = useState(null);
  const [destinoTransferencia, setDestinoTransferencia] = useState('');
  const [transferindo, setTransferindo] = useState(false);

  const abrirTransferir = (p) => {
    setOrigemTransferencia(p);
    setDestinoTransferencia('');
    setModalTransferirAberto(true);
  };

  const opcoesDestino = useMemo(
    () =>
      professores
        .filter((p) => p.ativo && p.id !== origemTransferencia?.id)
        .map((p) => ({ value: p.id, label: p.nome })),
    [professores, origemTransferencia],
  );

  const confirmarTransferencia = async () => {
    if (!destinoTransferencia || !origemTransferencia) return;
    setTransferindo(true);
    try {
      const resTurmas = await fetch(
        `${API_URL}/api/professores-admin/${origemTransferencia.id}/turmas`,
        { headers: authHeaders },
      );
      const dataTurmas = await resTurmas.json();
      if (!resTurmas.ok) throw new Error(dataTurmas.error || 'Erro ao buscar turmas do educador.');
      const turmas = dataTurmas?.data ?? (Array.isArray(dataTurmas) ? dataTurmas : []);

      const nomeDestino =
        professores.find((p) => String(p.id) === String(destinoTransferencia))?.nome || '';

      if (turmas.length === 0) {
        toast.info(`"${origemTransferencia.nome}" não tem nenhuma turma ativa pra transferir.`);
        setModalTransferirAberto(false);
        return;
      }

      const lista = turmas
        .map((t) => `• ${t.nome} (${t.dia_semana} ${t.horario}, ${t.turno})`)
        .join('\n');
      const confirmou = await confirm(
        `Transferir ${turmas.length} turma(s) de "${origemTransferencia.nome}" pra "${nomeDestino}"?\n\n${lista}\n\n"${origemTransferencia.nome}" deixa de ser o educador principal dessas turmas. Deseja continuar?`,
        { title: 'Confirmar transferência de turmas', confirmText: 'Transferir', tone: 'danger' },
      );
      if (!confirmou) return;

      const res = await fetch(
        `${API_URL}/api/professores-admin/${origemTransferencia.id}/transferir`,
        {
          method: 'POST',
          headers: authHeaders,
          body: JSON.stringify({ id_destino: Number(destinoTransferencia) }),
        },
      );
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Erro ao transferir turmas.');

      const qtd = data?.data?.turmas_transferidas ?? data?.turmas_transferidas ?? turmas.length;
      toast.success(data?.message || `${qtd} turma(s) transferida(s) pra "${nomeDestino}".`);
      setModalTransferirAberto(false);
      recarregar();
      queryClient.invalidateQueries({ queryKey: ['turmas', institutionId] });
    } catch (error) {
      toast.error(error.message);
    } finally {
      setTransferindo(false);
    }
  };

  return (
    <div className="min-h-screen w-full bg-[#F9FAFB] dark:bg-gray-950">
      <main className="p-4 max-w-4xl mx-auto space-y-6">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div className="w-full sm:w-80">
            <Input
              placeholder="Buscar educador..."
              icon={Search}
              value={busca}
              onChange={(e) => setBusca(e.target.value)}
            />
          </div>
          <div className="flex items-center gap-3">
            {filtrosColunaAtivos && (
              <button
                type="button"
                onClick={limparFiltrosColuna}
                className="text-xs font-medium text-blue-600 dark:text-blue-400 hover:underline"
              >
                Limpar filtros
              </button>
            )}
            {podeCriar && (
              <Button variant="primary" icon={Plus} onClick={abrirNovo}>
                Novo Educador
              </Button>
            )}
          </div>
        </div>

        <Card padding="none" className="overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-gray-50 dark:bg-gray-800/60">
                <tr>
                  <th className="px-4 py-2.5 text-left font-medium text-gray-500 dark:text-gray-400 text-xs">
                    Educador
                  </th>
                  <th className="px-4 py-2.5 text-center font-medium text-gray-500 dark:text-gray-400 text-xs">
                    Status
                    <FiltroColunaSimples
                      opcoes={opcoesStatus}
                      excluidos={filtrosColuna.status}
                      onChange={(v) => alterarFiltroColuna('status', v)}
                    />
                  </th>
                  <th className="px-4 py-2.5 text-center font-medium text-gray-500 dark:text-gray-400 text-xs">
                    Turmas Ativas
                    <FiltroColunaSimples
                      opcoes={opcoesTurmas}
                      excluidos={filtrosColuna.turmas}
                      onChange={(v) => alterarFiltroColuna('turmas', v)}
                    />
                  </th>
                  <th className="px-4 py-2.5 text-center font-medium text-gray-500 dark:text-gray-400 text-xs">
                    Inscritos
                    <FiltroColunaSimples
                      opcoes={opcoesInscritos}
                      excluidos={filtrosColuna.inscritos}
                      onChange={(v) => alterarFiltroColuna('inscritos', v)}
                    />
                  </th>
                  <th className="px-4 py-2.5 text-right font-medium text-gray-500 dark:text-gray-400 text-xs">
                    Ações
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100 dark:divide-gray-800">
                {isLoading ? (
                  <tr>
                    <td
                      colSpan={5}
                      className="px-4 py-12 text-center text-gray-500 dark:text-gray-400"
                    >
                      <Loader2 className="w-5 h-5 animate-spin inline mr-2" />
                      Carregando educadores...
                    </td>
                  </tr>
                ) : professoresFiltrados.length === 0 ? (
                  <tr>
                    <td
                      colSpan={5}
                      className="px-4 py-12 text-center text-gray-400 dark:text-gray-500 text-sm"
                    >
                      {busca || filtrosColunaAtivos ? (
                        <>
                          Nenhum educador encontrado para esses filtros.{' '}
                          <button
                            type="button"
                            onClick={() => {
                              setBusca('');
                              limparFiltrosColuna();
                            }}
                            className="text-blue-600 dark:text-blue-400 hover:underline"
                          >
                            Limpar filtros
                          </button>
                        </>
                      ) : (
                        'Nenhum educador cadastrado.'
                      )}
                    </td>
                  </tr>
                ) : (
                  professoresFiltrados.map((p) => (
                    <tr
                      key={p.id}
                      className={`hover:bg-gray-50/50 dark:hover:bg-gray-800/40 ${!p.ativo ? 'opacity-60' : ''}`}
                    >
                      <td className="px-4 py-2.5" translate="no">
                        <p className="font-medium text-gray-900 dark:text-gray-100">{p.nome}</p>
                        {p.nome_completo && (
                          <p className="text-xs text-gray-400 dark:text-gray-500">
                            {p.nome_completo}
                          </p>
                        )}
                      </td>
                      <td className="px-4 py-2.5 text-center">
                        <Badge tone={p.ativo ? 'success' : 'neutral'}>
                          {p.ativo ? 'Ativo' : 'Inativo'}
                        </Badge>
                      </td>
                      <td className="px-4 py-2.5 text-center">
                        <Badge tone={p.total_turmas_ativas > 0 ? 'info' : 'neutral'}>
                          {p.total_turmas_ativas}
                        </Badge>
                      </td>
                      <td className="px-4 py-2.5 text-center">
                        <Badge tone="neutral">{p.total_alunos}</Badge>
                      </td>
                      <td className="px-4 py-2.5">
                        <div className="flex items-center justify-end gap-1">
                          {podeEditar && (
                            <button
                              onClick={() => abrirTransferir(p)}
                              title="Transferir turmas pra outro educador"
                              disabled={p.total_turmas_ativas === 0}
                              className="w-8 h-8 rounded-lg hover:bg-blue-50 dark:hover:bg-blue-900/30 flex items-center justify-center text-gray-500 dark:text-gray-400 hover:text-blue-600 dark:hover:text-blue-400 transition-colors disabled:opacity-30 disabled:pointer-events-none"
                            >
                              <ArrowRightLeft className="w-4 h-4" />
                            </button>
                          )}
                          {podeEditar && (
                            <button
                              onClick={() => abrirEditar(p)}
                              title="Renomear"
                              className="w-8 h-8 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-800 flex items-center justify-center text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200 transition-colors"
                            >
                              <Pencil className="w-4 h-4" />
                            </button>
                          )}
                          {podeEditar && (
                            <button
                              onClick={() => alternarAtivo(p)}
                              title={p.ativo ? 'Desativar' : 'Reativar'}
                              className={`w-8 h-8 rounded-lg flex items-center justify-center text-gray-500 dark:text-gray-400 transition-colors ${p.ativo ? 'hover:bg-amber-50 dark:hover:bg-amber-900/30 hover:text-amber-600 dark:hover:text-amber-400' : 'hover:bg-green-50 dark:hover:bg-green-900/30 hover:text-green-600 dark:hover:text-green-400'}`}
                            >
                              {p.ativo ? (
                                <PowerOff className="w-4 h-4" />
                              ) : (
                                <Power className="w-4 h-4" />
                              )}
                            </button>
                          )}
                          {podeExcluir && (
                            <button
                              onClick={() => apagar(p)}
                              title={
                                p.total_turmas_ativas > 0
                                  ? 'Não é possível apagar: tem turma ativa'
                                  : 'Apagar'
                              }
                              className="w-8 h-8 rounded-lg hover:bg-red-50 dark:hover:bg-red-900/30 flex items-center justify-center text-gray-500 dark:text-gray-400 hover:text-red-500 dark:hover:text-red-400 transition-colors"
                            >
                              <Trash2 className="w-4 h-4" />
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </Card>
      </main>

      {/* Modal: Criar / Renomear */}
      <Modal
        isOpen={modalAberto}
        onClose={() => setModalAberto(false)}
        title={editando ? 'Renomear Educador' : 'Novo Educador'}
        maxWidth="sm"
      >
        <form onSubmit={salvar} className="space-y-4">
          <Input
            label="Nome"
            value={nomeForm}
            onChange={(e) => setNomeForm(e.target.value)}
            required
            autoFocus
          />
          <div className="space-y-1">
            <Input
              label="Nome completo (opcional)"
              value={nomeCompletoForm}
              onChange={(e) => setNomeCompletoForm(e.target.value)}
              placeholder="Ex.: Albert Andrade Cerqueira de Moura"
            />
            <p className="text-xs text-gray-400 dark:text-gray-500">
              Usado só no Relatório de Atividades em PDF — o nome curto acima continua aparecendo no
              resto do sistema.
            </p>
          </div>
          <div className="flex justify-end gap-2 pt-2">
            <Button type="button" variant="secondary" onClick={() => setModalAberto(false)}>
              Cancelar
            </Button>
            <Button type="submit" variant="primary" icon={Save} disabled={salvando}>
              {salvando ? 'Salvando...' : 'Salvar'}
            </Button>
          </div>
        </form>
      </Modal>

      {/* Modal: Transferir turmas */}
      <Modal
        isOpen={modalTransferirAberto}
        onClose={() => setModalTransferirAberto(false)}
        title={`Transferir turmas de ${origemTransferencia?.nome || ''}`}
        maxWidth="sm"
      >
        <div className="space-y-4">
          <p className="text-sm text-gray-600 dark:text-gray-300">
            Escolha pra quem transferir todas as {origemTransferencia?.total_turmas_ativas} turma(s)
            ativas onde "{origemTransferencia?.nome}" é o educador principal.
          </p>
          <Select
            label="Transferir para"
            value={destinoTransferencia}
            onChange={(e) => setDestinoTransferencia(e.target.value)}
            options={[{ value: '', label: 'Selecione...' }, ...opcoesDestino]}
          />
          <div className="flex justify-end gap-2 pt-2">
            <Button
              type="button"
              variant="secondary"
              onClick={() => setModalTransferirAberto(false)}
            >
              Cancelar
            </Button>
            <Button
              type="button"
              variant="primary"
              icon={ArrowRightLeft}
              disabled={!destinoTransferencia || transferindo}
              onClick={confirmarTransferencia}
            >
              {transferindo ? 'Transferindo...' : 'Continuar'}
            </Button>
          </div>
        </div>
      </Modal>
    </div>
  );
}

export default Professores;
